import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import moment from 'moment'

import { Transition } from '@headlessui/react'

import { Badge, Typography } from '@hubii/ds-hubii'

import { LoadOrdersToAnswer } from '@services/hubii/hub-service/types'
import { useNotifications } from '@stores/notification.context/notification.context'

import { shippingMethodStatusColors } from '@pages/order-list/helpers'

import {
  deliveryMethodsLabels,
  orderDeliveryShippingIcon,
  EOrderShippingMethod,
} from '../../../types'
import { BadgeProps } from '@hubii/ds-hubii/lib/components/Badge/Badge'
import { IconRefreshArrows } from '@hubii/icons'

type Props = {
  order: LoadOrdersToAnswer.Response['data'][0] & { orderType: EOrderShippingMethod }
}

const NotificationCard: React.FC<Props> = ({ order }) => {
  const navigate = useNavigate()
  const { audio } = useNotifications()

  const dateToAnswer = new Date(order.answerLeadTime)
  const remaining = dateToAnswer.getTime() - Date.now()
  const [timeLeft, setTimeLeft] = useState(remaining)

  useEffect(() => {
    const updateTimer = () => {
      const remaining = dateToAnswer.getTime() - Date.now()
      setTimeLeft(remaining > 0 ? remaining : 0)
    }

    updateTimer()

    //call again with 1 second delay
    const timer = setInterval(updateTimer, 1000)
    return () => clearInterval(timer)
  }, [order.answerLeadTime])

  const handleProposalClick = (orderUuid: string) => {
    audio?.pause()
    navigate(`/pedidos/${orderUuid}`)
  }

  return (
    <Transition
      show
      appear
      enter='duration-1000'
      enterFrom='translate-x-full'
      enterTo='translate-x-0'>
      <div
        role='button'
        onClick={() => handleProposalClick(order.uuid)}
        className='bg-white transition-colors hover:bg-gray-100 p-3.5 border-b border-gray-100 flex flex-col gap-3.5'>
        <Typography variant='subtitle2'>Pedido #{order.id}</Typography>
        <div className='flex items-center gap-2'>
          <Badge
            dataTestId='badge'
            variant='outlined'
            status={
              shippingMethodStatusColors[
                order.orderType as keyof typeof shippingMethodStatusColors
              ] as BadgeProps['status']
            }
            size='small'
            icon={orderDeliveryShippingIcon[order.orderType]}
            text={deliveryMethodsLabels[order.orderType]}
          />
        </div>

        <div className='flex items-center justify-between'>
          <div className='flex gap-1'>
            {order.answerLeadTime ? (
              <>
                <div className='flex flex-col items-center'>
                  <Badge
                    dataTestId='badge-time-left'
                    disabled={timeLeft <= 0}
                    status={timeLeft <= 0 ? 'neutral' : 'warning'}
                    className='w-[calc(2ch+1.25rem)] aspect-1 justify-center'
                    text={moment.duration(timeLeft, 'milliseconds').minutes()}
                  />

                  <Typography variant='caption' color='textSecondary'>
                    min
                  </Typography>
                </div>
                <Typography variant='body1' className='mt-1'>
                  :
                </Typography>
                <div className='flex flex-col items-center'>
                  <Badge
                    dataTestId='badge-time-left'
                    disabled={timeLeft <= 0}
                    status={timeLeft <= 0 ? 'neutral' : 'warning'}
                    className='w-[calc(2ch+1.25rem)] aspect-1 justify-center'
                    text={moment.duration(timeLeft, 'milliseconds').seconds()}
                  />
                  <Typography variant='caption' color='textSecondary'>
                    seg
                  </Typography>
                </div>
              </>
            ) : (
              <Badge
                dataTestId='badge-time-left'
                size='xsmall'
                disabled
                status='neutral'
                variant='default'
                text='Redirecionado'
                icon={IconRefreshArrows}
              />
            )}
          </div>
          <div className='flex -space-x-3 overflow-hidden'>
            <div className='inline-block h-8 w-8 rounded-full ring-2 ring-white p-1.5 bg-slate-100 object-cover overflow-hidden'>
              <img
                className='w-full h-full object-cover rounded'
                src={order.items[0].image}
                alt={order.items[0].name}
              />
            </div>
            {order.items.length > 1 && (
              <div className='flex justify-center items-center h-8 w-8 rounded-full ring-3 ring-white bg-slate-100 object-cover overflow-hidden'>
                <Typography className='cursor-pointer' variant='body2' color='primary'>
                  + {order.items.length - 1}
                </Typography>
              </div>
            )}
          </div>
        </div>
      </div>
    </Transition>
  )
}

export default NotificationCard
